import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageWrap, FadeIn } from '../components/Section';

const services = [
  { title: 'Safe & Ultimate Bearing Capacity (SBC)', desc: 'Using shear strength parameters and penetration tests to calculate net safe bearing capacity, guiding foundation design. Based on the classical Terzaghi bearing capacity equation.', tags: ['Foundation design', 'High-rise structures', 'Bridge piers'] },
  { title: 'California Bearing Ratio (CBR) Testing', desc: 'Evaluating the subgrade strength of soil for road pavements and highway design — a critical parameter for pavement thickness calculations.', tags: ['NHAI road projects', 'Municipal roads', 'Airport runways'] },
  { title: 'Compaction & Density Parameters', desc: 'Determining Maximum Dry Density (MDD) and Optimum Moisture Content (OMC) through standard and modified Proctor compaction tests.', tags: ['Embankment construction', 'Dam fills', 'Earthwork QC'] },
  { title: 'Subsurface Exploration & Boring', desc: 'Utilizing borehole drilling, Standard Penetration Tests (SPT), and Dynamic Cone Penetration Tests (DCPT) to map soil strata and groundwater depth.', tags: ['Site characterization', 'Foundation planning', 'Groundwater mapping'] },
];

export default function Geotechnical() {
  return (
    <PageWrap>
      <section style={{ padding: '160px 0 80px', background: 'var(--warm)' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--primary)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 16 }}>Services</p>
            <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 20 }}>
              Geotechnical Investigation<br /><span style={{ color: 'var(--primary-light)' }}>& Soil Mechanics</span>
            </h1>
            <p style={{ fontSize: '1.08rem', color: 'var(--text-mid)', maxWidth: 620, lineHeight: 1.7, marginBottom: 32 }}>
              Before any structural design can begin, the engineering properties of the site's soil must be characterized. Our comprehensive geotechnical evaluations determine allowable load-bearing capacity and prevent structural failures.
            </p>
            <Link to="/contact" className="btn btn-primary">Schedule Field Investigation</Link>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section style={{ padding: '100px 0', background: 'var(--white)' }}>
        <div className="container">
          <FadeIn>
            <h2 style={{ fontSize: '2.2rem', marginBottom: 12 }}>Testing & Investigation Services</h2>
            <p style={{ color: 'var(--text-light)', marginBottom: 48, maxWidth: 560 }}>NABL-accredited field and laboratory testing for complete geotechnical characterization.</p>
          </FadeIn>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {services.map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.08}>
                <div style={{ display: 'flex', gap: 32, padding: 40, background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', transition: 'all 0.3s', flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: '2.5rem', fontWeight: 700, color: 'var(--border)', width: 60, flexShrink: 0 }}>{String(i + 1).padStart(2, '0')}</div>
                  <div style={{ flex: 1, minWidth: 250 }}>
                    <h3 style={{ fontSize: '1.2rem', marginBottom: 10 }}>{s.title}</h3>
                    <p style={{ fontSize: '0.93rem', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 16 }}>{s.desc}</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {s.tags.map(t => (
                        <span key={t} style={{ padding: '5px 12px', background: 'rgba(45,74,45,0.06)', borderRadius: 4, fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 450 }}>{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Formula */}
      <section style={{ padding: '100px 0', background: 'var(--cream)' }}>
        <div className="container">
          <FadeIn>
            <div style={{ background: 'var(--primary-dark)', borderRadius: 20, padding: 'clamp(40px, 6vw, 60px)', textAlign: 'center' }}>
              <h3 style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.85)', marginBottom: 32 }}>Terzaghi Bearing Capacity Equation</h3>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(1.2rem, 3vw, 2rem)', color: 'var(--accent)', marginBottom: 32, letterSpacing: 1 }}>
                q<sub>u</sub> = cN<sub>c</sub>s<sub>c</sub> + qN<sub>q</sub> + 0.5γBN<sub>γ</sub>s<sub>γ</sub>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px 28px' }}>
                {[['c', 'Soil cohesion'], ['q', 'Overburden pressure'], ['γ', 'Unit weight'], ['B', 'Foundation width'], ['Nc, Nq, Nγ', 'Bearing capacity factors']].map(([sym, desc]) => (
                  <div key={sym} style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                    <strong style={{ color: 'var(--accent-light)' }}>{sym}</strong> — {desc}
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Audience & CTA */}
      <section style={{ padding: '100px 0', background: 'var(--white)' }}>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 60, alignItems: 'start' }}>
          <FadeIn>
            <h2 style={{ fontSize: '2.2rem', marginBottom: 16 }}>Who We Serve</h2>
            <p style={{ fontSize: '0.98rem', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: 24 }}>Our geotechnical services support projects across Madhya Pradesh and Central India.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {['Real Estate Developers', 'Infrastructure Contractors', 'Government Agencies (CPWD, NHAI)', 'Architects & Structural Consultants', 'Indian Railways', 'Municipal Corporations'].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.92rem', color: 'var(--text-mid)', padding: '12px 16px', background: 'var(--cream)', borderRadius: 6 }}>
                  <span style={{ color: 'var(--primary)', fontWeight: 700 }}>✓</span> {item}
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div style={{ background: 'var(--warm)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 40 }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: 12 }}>Need Soil Investigation?</h3>
              <p style={{ fontSize: '0.95rem', color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: 24 }}>
                Our team deploys to project sites across Madhya Pradesh, UP, and Central India for comprehensive geotechnical surveys.
              </p>
              <Link to="/contact" className="btn btn-primary">Request On-Site Quote</Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </PageWrap>
  );
}
